// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchReferringProvider } from '../utils/patient-referring-provider';

export interface UsePatientReferringProviderResult {
  practitioner: Practitioner | undefined;
  organization: Organization | undefined;
  loading: boolean;
  error: Error | null;
  setReferringProvider: (practitioner: Practitioner, organization: Organization) => void;
  refresh: () => void;
}

export function usePatientReferringProvider(patient: Patient | undefined): UsePatientReferringProviderResult {
  const medplum = useMedplum();
  const [practitioner, setPractitioner] = useState<Practitioner | undefined>();
  const [organization, setOrganization] = useState<Organization | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReferringProvider(medplum, patient);
      setPractitioner(result.practitioner);
      setOrganization(result.organization);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load referring provider'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  const setReferringProvider = useCallback((p: Practitioner, o: Organization) => {
    setPractitioner(p);
    setOrganization(o);
  }, []);

  return { practitioner, organization, loading, error, setReferringProvider, refresh: fetch };
}
